import { typographyTokens } from '../../../../theme/typographyTokens';
/**
 * SEPEditModal Component
 * 
 * Modal dialog for editing SEP fields including name, phase, status,
 * description, validation date, and completion date.
 * 
 * Date Constraints:
 * - Validation Date: min = startedAt, max = today
 * - Completed At: min = validationDate (or startedAt if no validationDate), max = today
 * 
 * @module SEPEditModal
 * @path src/frontend/features/Admin/Others/SEPDetails/SEPEditModal.tsx
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { UpdateSEPBody } from '../../../../../shared/types/SEP';

/**
 * Interface for SEP edit form data
 * Contains all editable fields plus startedAt for date validation
 */
export interface SEPEditFormData {
  id: number;
  name: string;
  description: string | null;
  completedAt: string | null;
  validationDate: string | null;
  phase: string;
  status: string;
  /** Used for date picker constraints (not editable) */
  startedAt: string | null;
}

/**
 * Props interface for SEPEditModal
 * @property {boolean} open - Controls modal visibility
 * @property {function} onClose - Callback to close the modal
 * @property {SEPEditFormData | null} sepData - Initial SEP data for the form
 * @property {function} updateSep - RTK Query mutation function
 * @property {any} theme - MUI theme object
 */
interface SEPEditModalProps {
  open: boolean;
  onClose: () => void;
  sepData: SEPEditFormData | null;
  updateSep: (data: { id: number; body: UpdateSEPBody }) => void;
  theme: any;
}

/**
 * Formats a date string to YYYY-MM-DD format for HTML date input
 * @param {string | null} dateString - ISO date string or null
 * @returns {string} Formatted date string or empty string if null
 */
const formatDateForInput = (dateString: string | null): string => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toISOString().split('T')[0];
};

/** Available SEP phase options */
const PHASE_OPTIONS = ['knockout', 'validation', 'initiate', 'design', 'complete'] as const;

/** Available SEP status options */
const STATUS_OPTIONS = ['active', 'approved', 'cancelled', 'expired', 'rejected'] as const;

/** Initial empty form state */
const INITIAL_FORM_STATE: SEPEditFormData = {
  id: 0,
  name: '',
  description: '',
  completedAt: '',
  validationDate: '',
  phase: '',
  status: '',
  startedAt: '',
};

/**
 * SEPEditModal - Modal form for editing SEP details
 * 
 * Features:
 * - Editable fields: name, phase, status, description, validationDate, completedAt
 * - Date pickers with min/max constraints based on startedAt and validationDate
 * - Only sends changed fields to API (optimization)
 * - Capitalizes dropdown option labels
 * 
 * @param {SEPEditModalProps} props - Component props
 * @returns {JSX.Element | null} The edit modal or null if no data
 */
const SEPEditModal: React.FC<SEPEditModalProps> = ({ open, onClose, sepData, updateSep, theme }) => {
  // Form state initialized with empty values
  const [formData, setFormData] = useState<SEPEditFormData>(INITIAL_FORM_STATE);

  /**
   * Sync form state with sepData when modal opens or data changes
   * Formats dates for HTML date input compatibility
   */
  useEffect(() => {
    if (sepData) {
      setFormData({
        id: sepData.id,
        name: sepData.name || '',
        description: sepData.description || '',
        completedAt: formatDateForInput(sepData.completedAt),
        validationDate: formatDateForInput(sepData.validationDate),
        phase: sepData.phase || '',
        status: sepData.status || '',
        startedAt: formatDateForInput(sepData.startedAt),
      });
    }
  }, [sepData]);

  /**
   * Generic change handler for form fields
   * @param {keyof SEPEditFormData} field - Field name to update
   * @returns {function} Event handler function
   */
  const handleChange = useCallback((field: keyof SEPEditFormData) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: (event.target as any).value,
    }));
  }, []);

  /**
   * Handles form submission
   * Only includes changed fields in the API request body (optimization)
   * Converts date strings to Date objects or null for clearing
   */
  const handleSave = useCallback(() => {
    // Start with always-included fields
    const body: UpdateSEPBody = {
      name: formData.name,
      description: formData.description,
    };

    // Only include phase if it was changed
    if (formData.phase !== sepData?.phase) {
      (body as any).phase = formData.phase;
    }

    // Only include status if it was changed
    if (formData.status !== sepData?.status) {
      (body as any).status = formData.status;
    }

    // Only include completedAt if it was changed (supports clearing with null)
    const originalCompletedAt = formatDateForInput(sepData?.completedAt ?? null);
    if (formData.completedAt !== originalCompletedAt) {
      (body as any).completedAt = formData.completedAt ? new Date(formData.completedAt) : null;
    }

    // Only include validationDate if it was changed (supports clearing with null)
    const originalValidationDate = formatDateForInput(sepData?.validationDate ?? null);
    if (formData.validationDate !== originalValidationDate) {
      body.validationDate = formData.validationDate || null;
    }

    // Call the mutation and close modal
    updateSep({
      id: formData.id,
      body,
    });

    onClose();
  }, [formData, sepData, updateSep, onClose]);

  /**
   * Memoized today's date string for date input max constraint
   */
  const todayDate = useMemo(() => new Date().toISOString().split('T')[0], []);

  /**
   * Memoized min date for completedAt picker
   * Uses validationDate if set, otherwise falls back to startedAt
   */
  const completedAtMinDate = useMemo(() => {
    return formData.validationDate || formData.startedAt || undefined;
  }, [formData.validationDate, formData.startedAt]);

  // Don't render if no data provided
  if (!sepData) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: typographyTokens.fontWeights.semibold }}>
          Edit SEP - {sepData.id}
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ pt: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          {/* Name Field */}
          <TextField
            label="Name"
            value={formData.name}
            onChange={handleChange('name')}
            fullWidth
            variant="outlined"
            size="small"
          />
          
          {/* Phase Dropdown */}
          <TextField
            label="Phase"
            select
            value={formData.phase}
            onChange={handleChange('phase')}
            fullWidth
            variant="outlined"
            size="small"
          >
            {PHASE_OPTIONS.map((phase) => (
              <MenuItem key={phase} value={phase}>
                {phase.charAt(0).toUpperCase() + phase.slice(1)}
              </MenuItem>
            ))}
          </TextField>
          
          {/* Status Dropdown */}
          <TextField
            label="Status"
            select
            value={formData.status}
            onChange={handleChange('status')}
            fullWidth
            variant="outlined"
            size="small"
          >
            {STATUS_OPTIONS.map((status) => (
              <MenuItem key={status} value={status}>
                {status.charAt(0).toUpperCase() + status.slice(1)}
              </MenuItem>
            ))}
          </TextField>
          
          {/* Description Textarea */}
          <TextField
            label="Description"
            value={formData.description}
            onChange={handleChange('description')}
            fullWidth
            variant="outlined"
            multiline
            rows={11}
            placeholder="Enter SEP description..."
          />
          
          {/* Validation Date Picker - min: startedAt, max: today */}
          <TextField
            label="Validation Date"
            type="date"
            value={formData.validationDate}
            onChange={handleChange('validationDate')}
            fullWidth
            variant="outlined"
            size="small"
            InputLabelProps={{
              shrink: true,
            }}
            inputProps={{
              min: formData.startedAt || undefined,
              max: todayDate,
            }}
          />
          
          {/* Completed Date Picker - min: validationDate or startedAt, max: today */}
          <TextField
            label="Completed At"
            type="date"
            value={formData.completedAt}
            onChange={handleChange('completedAt')}
            fullWidth
            variant="outlined"
            size="small"
            InputLabelProps={{
              shrink: true,
            }}
            inputProps={{
              min: completedAtMinDate,
              max: todayDate,
            }}
          />
        </Box>
      </DialogContent>
      <Divider />
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          variant="outlined"
          onClick={onClose}
          sx={{ textTransform: 'none' }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          sx={{ textTransform: 'none', backgroundColor: theme.palette.primary.main }}
        >
          Save Changes
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SEPEditModal;
